import _ from 'lodash';
import { RadioButton } from "@kursk/components/ui/RadioButton/RadioButton";
import {
  ArrowDownIcon,
  CloseIconSmall,
  FiltersBadge,
  FiltersIcon,
  FiltersIconBel,
} from '@kursk/components/ui/icons';
import classNames from 'classnames';
import React, { ChangeEvent, MouseEvent, useState } from 'react';
import styles from './MobileFilters.module.scss';
// eslint-disable-next-line max-len
import { CheckboxSelectMobileFilter } from '@kursk/components/ui/Filters/CheckboxSelectMobileFilter/CheckboxSelectMobileFilter';
// eslint-disable-next-line max-len
import { CheckboxSelectMobileFilterSimple } from '@kursk/components/ui/Filters/CheckboxSelectMobileFilterSimple/CheckboxSelectMobileFilterSimple';
import { Checkbox } from '@kursk/components/ui/Checkbox/Checkbox';
import { EntityType, ILocale } from '@common/redux/types/common';
import { IPlace } from '@common/redux/types/places';
import { IRange, RangeFilter } from "@kursk/components/ui/Filters/RangeFilter/RangeFilter";
import { IFilters } from "@common/redux/slices/entities.slice";
import { themeValue } from "@kursk/themes";
import { ICheckboxSelectOption } from '../../CheckboxSelect/CheckboxSelect';
import { ICheckboxSelectSimpleOption } from '../../CheckboxSelectSimple/CheckboxSelectSimple';
import { getSellersAndSubjectsValues } from "@kursk/components/ui/Filters/MobileFilters/helpers/helpers";
import { regionContent } from "@kursk/content/regionContent";

interface IProps {
  close: () => void;
  open: () => void;
  isOpen: boolean;
  filters: IFilters;
  entityType: EntityType;
  updateStreams?: (
    places: boolean,
    selectedPlaces: number[],
    streamsRadioValue: string) => Promise<void>;
  updateMobileFilter?: (
    locales: boolean,
    places: boolean,
    audio: boolean,
    transportations: boolean,
    organizations: boolean,
    sellerNames: boolean,
    subjectNames: boolean,
    selectedLocales: number[],
    selectedPlaces: number[],
    hasAudio: boolean,
    selectedTransportations: string[],
    selectedOrganizations: string[],
    selectedSellerNames: string[],
    selectedSubjectNames: string[],
  ) => Promise<void>;
  allLocales?: ILocale[];
  disabledPlaces?: IPlace[];
  disabledLocales?: ILocale[];
  allPlaces?: IPlace[];
  withLocales?: boolean;
  withPlaces?: boolean;
  withPrices?: boolean;
  withPricesOptions?: {
    pricesRange?: IRange;
    withFreeCheckbox?: boolean;
    withTicketsCheckbox?: boolean;
    dropDownTitle?: string;
    selectedRangeValue: IRange;
    setSelectedRangeValue?: React.Dispatch<React.SetStateAction<IRange | undefined>>;
    withTickets?: boolean;
    setWithTickets?: React.Dispatch<React.SetStateAction<boolean>>;
    isFree?: boolean;
    setIsFree?: React.Dispatch<React.SetStateAction<boolean>>;
  };
  withAudio?: boolean;
  withDuration?: boolean;
  withDurationOptions?: {
    durationsRange?: IRange;
    dropDownTitle?: string;
    selectedRangeValue: IRange;
    setSelectedRangeValue?: React.Dispatch<React.SetStateAction<IRange | undefined>>;
  };
  withTransportation?: boolean;
  withTransportationOptions?: {
    items?: ICheckboxSelectSimpleOption[];
    filterField?: string;
  };
  withOrganizations?: boolean;
  withOrganizationsOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
  };
  withSellerNames?: boolean;
  withSellerNamesOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
    withSearch: boolean;
  };
  withSubjectNames?: boolean;
  withSubjectNamesOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
    withSearch: boolean;
  };
}

enum Step {
  'menu',
  'localesFilter',
  'placesFilter',
  'priceFilter',
  'durationFilter',
  'transportationFilter',
  'organizationsFilter',
  'sellerNamesFilter',
  'subjectNamesFilter'
}

const getStreamsRadioInitValue = (filters) => {
  if (filters.isLive) {
    return 'liveStreams';
  } else if (filters.isPreview) {
    return 'previewStreams';
  } else if (filters.isArchive) {
    return 'archiveStreams';
  } else {
    return 'allStreams';
  }
}

const transportationMethodTitle = regionContent.filters?.touristroutes?.transportationMethodTitle
  ?? 'Способ передвижения';

export function MobileFilters({
  close,
  allLocales,
  allPlaces,
  open,
  isOpen,
  withLocales,
  withPrices,
  withPricesOptions,
  withPlaces,
  withAudio,
  entityType,
  updateMobileFilter,
  updateStreams,
  filters,
  disabledPlaces,
  disabledLocales,
  withDuration,
  withDurationOptions,
  withTransportation,
  withTransportationOptions,
  withOrganizations,
  withOrganizationsOptions,
  withSellerNames,
  withSellerNamesOptions,
  withSubjectNames,
  withSubjectNamesOptions
}: IProps) {
  const transportationMethodsFilter = withTransportationOptions ? filters[withTransportationOptions.filterField] : [];
  const organizationsFilter = withOrganizationsOptions ? filters[withOrganizationsOptions.filterField] : [];
  const sellerNamesFilter = getSellersAndSubjectsValues(filters, withSellerNamesOptions);
  const subjectNamesFilter = getSellersAndSubjectsValues(filters, withSubjectNamesOptions);
  const [streamsRadioValue, setStreamsRadioValue] = useState(getStreamsRadioInitValue(filters));
  const [selectedLocales, setSelectedLocales] = useState(filters.locales);
  const [selectedPlaces, setSelectedPlaces] = useState(filters.places);
  const [selectedTransportations, setSelectedTransportations] = useState(transportationMethodsFilter);
  const [selectedOrganizations, setSelectedOrganizations] = useState(organizationsFilter);
  const [selectedSellerNames, setSelectedSellerNames] = useState(sellerNamesFilter);
  const [selectedSubjectNames, setSelectedSubjectNames] = useState(subjectNamesFilter);
  const [hasAudio, setHasAudio] = useState(filters.hasAudio);
  const [step, setStep] = useState(Step.menu);

  const apply = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    await update();

    close();
  }

  const update = async () => {
    if (entityType !== 'streams') {
      if (updateMobileFilter) {
        await updateMobileFilter(
          withLocales, withPlaces, withAudio, withTransportation, withOrganizations,
          withSellerNames, withSubjectNames,
          selectedLocales, selectedPlaces, hasAudio, selectedTransportations, selectedOrganizations,
          selectedSellerNames, selectedSubjectNames
        );
      }
    } else {
      await updateStreams(withPlaces, selectedPlaces, streamsRadioValue);
    }
  };

  const onLocalesChange = (values) => {
    setSelectedLocales(_(values).map(Number).reject(_.isNaN).value());
  };

  const onPlacesChange = (values) => {
    setSelectedPlaces(_(values).map(Number).reject(_.isNaN).value());
  };

  const onStreamsRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStreamsRadioValue(event.target.value)
  };

  const onHasAudioClick = () => {
    setHasAudio(!hasAudio);
  }

  const onHasAudioChange = (event) => {
    setHasAudio(!event.target?.checked);
  }

  const selectedPriceFilters = (): string => {
    const selectedFilters: string[] = [];
    const { selectedRangeValue, isFree, withTickets } = withPricesOptions;
    if (isFree) {
      selectedFilters.push('Бесплатно');
    }
    if (selectedRangeValue && !isFree) {
      selectedFilters.push(`${selectedRangeValue.min}–${selectedRangeValue.max}`);
    }
    if (withTickets) {
      selectedFilters.push('С билетами');
    }
    return selectedFilters.join(', ');
  };

  const getDurationString = () => {
    const selectedFilters: string[] = [];
    const selectedRangeValue = withDurationOptions.selectedRangeValue;

    if (selectedRangeValue?.max >= 0 || filters?.durationTo >= 0) {
      const durationFrom = selectedRangeValue?.min || 0;
      const durationTo = selectedRangeValue?.max || 0;
      selectedFilters.push(`${durationFrom}–${durationTo}`);
    }
    return selectedFilters.join(', ');
  };

  const getTransportationString = () => {
    const selectedFilters = selectedTransportations.map((selectedItem) => {
      const actualItem = withTransportationOptions.items.find((item) => item.value == selectedItem);
      return actualItem?.name;
    })

    return selectedFilters.join(', ');
  };

  const getSellerNamesString = () => {
    const selectedFilters = selectedSellerNames.map((selectedItem) => {
      const actualItem = withSellerNamesOptions.items.find((item) => item._id == selectedItem);
      return actualItem?.name;
    })

    return selectedFilters.join(', ');
  };

  const getSubjectNamesString = () => {
    const selectedFilters = selectedSubjectNames.map((selectedItem) => {
      const actualItem = withSubjectNamesOptions.items.find((item) => item._id == selectedItem);
      return actualItem?.name;
    })

    return selectedFilters.join(', ');
  };

  const renderMenu = () => {
    const chosenLocalesNames = _(allLocales).filter((value) =>
      _.includes(selectedLocales, value._id)).map('name').join(', ');
    const chosenPlacesNames = _(allPlaces).filter((value) =>
      _.includes(selectedPlaces, value._id)).map('name').join(', ');

    return (
      <div className={classNames(styles.content, styles.menu)}>
        <div className={styles.title}>Фильтр</div>
        <button className={styles.closeIcon} onClick={close}>
          <CloseIconSmall width={14} height={14}/>
        </button>
        {withPrices && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.priceFilter)}
          >
            <div className={styles.blockTitle}>
              Цена
              {(withPricesOptions.selectedRangeValue || withPricesOptions.isFree || withPricesOptions.withTickets) && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {selectedPriceFilters()}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    withPricesOptions.setSelectedRangeValue(undefined);
                    withPricesOptions.setIsFree(false);
                    withPricesOptions.setWithTickets(false);
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        {entityType === 'streams' && (
          <>
            <div className={classNames(styles.block, styles.radioGroup)}>
              <RadioButton
                value={'allStreams'}
                checked={streamsRadioValue === 'allStreams'}
                onChange={onStreamsRadioChange}
                title={'Все трансляции'}
              />
              <RadioButton
                value={'liveStreams'}
                checked={streamsRadioValue === 'liveStreams'}
                onChange={onStreamsRadioChange}
                title={'В эфире'}
              />
              <RadioButton
                value={'previewStreams'}
                checked={streamsRadioValue === 'previewStreams'}
                onChange={onStreamsRadioChange}
                title={'Анонс'}
              />
              <RadioButton
                value={'archiveStreams'}
                checked={streamsRadioValue === 'archiveStreams'}
                onChange={onStreamsRadioChange}
                title={'Архив'}
              />
            </div>
          </>
        )}
        {withLocales && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.localesFilter)}
          >
            <div className={styles.blockTitle}>
              Город или район
              {!!selectedLocales.length && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>{chosenLocalesNames}</div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    setSelectedLocales([]);
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>)}
        {withDuration && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.durationFilter)}
          >
            <div className={styles.blockTitle}>
              Длительность
              {(withDurationOptions.selectedRangeValue) && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {getDurationString()}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    withDurationOptions.setSelectedRangeValue(null);
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        {withTransportation && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.transportationFilter)}
          >
            <div className={styles.blockTitle}>
              {transportationMethodTitle}
              {!!selectedTransportations.length && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {getTransportationString()}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    setSelectedTransportations([])
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        {withOrganizations && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.organizationsFilter)}
          >
            <div className={styles.blockTitle}>
              Гид
              {!!selectedOrganizations.length && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {_(withOrganizationsOptions.items).filter((value) =>
                      _.includes(selectedOrganizations, value._id)).map('name').join(', ')}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    setSelectedOrganizations([])
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        {
          withPlaces && (
            <div
              className={classNames(styles.block, styles.withIcon)}
              onClick={() => setStep(Step.placesFilter)}
            >
              <div className={styles.blockTitle}>
                Место проведения
                {!!selectedPlaces.length && (
                  <div className={styles.blockTitleChosenFiltersContainer}>
                    <div className={styles.blockTitleChosenFilters}>{chosenPlacesNames}</div>
                    <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                      event.stopPropagation();
                      setSelectedPlaces([]);
                    }}>
                      <CloseIconSmall width={9} height={9}/>
                    </div>
                  </div>
                )}
              </div>
              <ArrowDownIcon width={12} height={5}/>
            </div>
          )
        }
        {
          withAudio && (
            <div
              className={classNames(styles.block, styles.withIcon)}
              onClick={onHasAudioClick}
            >
              <div className={styles.blockTitle}>
                Аудиогид
              </div>
              <Checkbox value="hasAudio" checked={hasAudio} onChange={onHasAudioChange} />
            </div>
          )
        }
        {withSellerNames && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.sellerNamesFilter)}
          >
            <div className={styles.blockTitle}>
              Продавец
              {!!selectedSellerNames.length && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {getSellerNamesString()}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    setSelectedSellerNames([])
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        {withSubjectNames && (
          <div
            className={classNames(styles.block, styles.withIcon)}
            onClick={() => setStep(Step.subjectNamesFilter)}
          >
            <div className={styles.blockTitle}>
              Категория товара
              {!!selectedSubjectNames.length && (
                <div className={styles.blockTitleChosenFiltersContainer}>
                  <div className={styles.blockTitleChosenFilters}>
                    {getSubjectNamesString()}
                  </div>
                  <div className={styles.blockTitleChosenFiltersClear} onClick={(event) => {
                    event.stopPropagation();
                    setSelectedSubjectNames([])
                  }}>
                    <CloseIconSmall width={9} height={9}/>
                  </div>
                </div>
              )}
            </div>
            <ArrowDownIcon width={12} height={5}/>
          </div>
        )}
        <div className={styles.buttonContainer}>
          <button className={styles.button} onClick={apply}>применить</button>
        </div>
      </div>
    )
  }

  const renderLocalesFilter = () => (
    <div className={styles.content}>
      <CheckboxSelectMobileFilter
        <ILocale>
        items={allLocales}
        selectedItems={selectedLocales}
        disabledArray={disabledLocales}
        onChange={onLocalesChange}
        getBack={() => setStep(Step.menu)}
        close={close}
        placeholder="Введите название района"
      />
    </div>
  );

  const renderPlacesFilter = () => (
    <div className={styles.content}>
      <CheckboxSelectMobileFilter
        <IPlace>
        items={allPlaces}
        selectedItems={selectedPlaces}
        disabledArray={disabledPlaces}
        onChange={onPlacesChange}
        getBack={() => setStep(Step.menu)}
        close={close}
        placeholder="Введите название места"
      />
    </div>
  );

  const renderPricesFilter = () => (
    <div className={styles.content}>
      <RangeFilter
        rangeValues={withPricesOptions.pricesRange}
        selectedRangeValue={withPricesOptions.selectedRangeValue}
        setSelectedRangeValue={withPricesOptions.setSelectedRangeValue}
        classNameWrapper={styles.priceWrapper}
        isMobile={true}
        getBack={() => setStep(Step.menu)}
        close={close}
        setIsFree={withPricesOptions.setIsFree}
        isFree={withPricesOptions.isFree}
        setWithTickets={withPricesOptions.setWithTickets}
        withTickets={withPricesOptions.withTickets}
        withFreeCheckbox={withPricesOptions.withFreeCheckbox}
        withTicketsCheckbox={withPricesOptions.withTicketsCheckbox}
        dropDownTitle={withPricesOptions.dropDownTitle}/>
    </div>
  );

  const renderDurationFilter = () => (
    <div className={styles.content}>
      <RangeFilter
        rangeValues={withDurationOptions.durationsRange}
        classNameWrapper={styles.priceWrapper}
        mobileRangeModalTitle="Длительность"
        isMobile={true}
        dropDownTitle={withDurationOptions.dropDownTitle}
        selectedRangeValue={withDurationOptions.selectedRangeValue}
        setSelectedRangeValue={withDurationOptions.setSelectedRangeValue}
        getBack={() => setStep(Step.menu)}
        close={close}
      />
    </div>
  );

  const renderTransportationFilter = () => (
    <div className={styles.content}>
      <CheckboxSelectMobileFilterSimple
        title={transportationMethodTitle}
        items={withTransportationOptions.items}
        selectedItems={selectedTransportations}
        onChange={setSelectedTransportations}
        getBack={() => setStep(Step.menu)}
        close={close}
      />
    </div>
  );

  const renderOrganizationsFilter = () => (
    <div className={styles.content}>
      <CheckboxSelectMobileFilter
        placeholder="Гид"
        items={withOrganizationsOptions.items}
        selectedItems={selectedOrganizations}
        onChange={setSelectedOrganizations}
        getBack={() => setStep(Step.menu)}
        close={close}
        disabledArray={[]}
      />
    </div>
  );

  const renderSellerNamesFilter = () => withSellerNamesOptions ? (
    <div className={styles.content}>
      <CheckboxSelectMobileFilterSimple
        title="Продавец"
        items={withSellerNamesOptions.items}
        selectedItems={selectedSellerNames}
        onChange={setSelectedSellerNames}
        getBack={() => setStep(Step.menu)}
        close={close}
      />
    </div>
  ) : null;

  const renderSubjectNamesFilter = () => withSubjectNamesOptions ? (
    <div className={styles.content}>
      <CheckboxSelectMobileFilterSimple
        title="Категория товара"
        items={withSubjectNamesOptions.items}
        selectedItems={selectedSubjectNames}
        onChange={setSelectedSubjectNames}
        getBack={() => setStep(Step.menu)}
        close={close}
      />
    </div>
  ) : null;

  const activeFiltersCount = _.compact([
    withPricesOptions?.isFree,
    withPricesOptions?.withTickets,
    withPricesOptions?.selectedRangeValue,
    withDurationOptions?.selectedRangeValue,
    selectedLocales.length,
    selectedPlaces.length,
    hasAudio,
    streamsRadioValue !== 'allStreams',
    selectedTransportations.length,
    selectedOrganizations.length,
    selectedSellerNames.length,
    selectedSubjectNames.length,
  ]).length;


  return (
    <div className={styles.filterWrapper}>
      <button className={styles.filtersIcon} onClick={open}>
        {themeValue({
          bel: <FiltersIconBel/>,
          default: <FiltersIcon/>
        })}
        {!!activeFiltersCount && <FiltersBadge count={activeFiltersCount}/>}
      </button>
      {isOpen && (
        <div className={styles.wrapper}>
          {step === Step.menu && renderMenu()}
          {step === Step.localesFilter && renderLocalesFilter()}
          {step === Step.placesFilter && renderPlacesFilter()}
          {step === Step.priceFilter && renderPricesFilter()}
          {step === Step.durationFilter && renderDurationFilter()}
          {step === Step.transportationFilter && renderTransportationFilter()}
          {step === Step.organizationsFilter && renderOrganizationsFilter()}
          {step === Step.sellerNamesFilter && renderSellerNamesFilter()}
          {step === Step.subjectNamesFilter && renderSubjectNamesFilter()}
        </div>
      )}
    </div>
  )
}
